import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button, Spinner } from '../atoms';

/**
 * GroupCustomFields — define + set the custom fields that belong to a GROUP.
 *
 * Group-level: one value per field, SHARED by every record in the group (e.g. the
 * group's "Review status"). Reused by the group's own detail page (`variant="full"`)
 * and, in a lighter form, by each record's Groups tab (`variant="compact"`) — so a
 * group's fields can be filled in from wherever you work with the group, not only on
 * the group page.
 */
export interface GroupField {
  definition_id: string;
  field_key: string;
  label: string;
  data_type: string;
  options: string[] | null;
  value: string | null;
  typed_value: unknown;
}

export function GroupCustomFields({
  groupId,
  canEdit,
  compact = false,
}: {
  groupId: string;
  canEdit: boolean;
  compact?: boolean;
}) {
  const qc = useQueryClient();
  const key = ['custom-fields', 'groups', groupId];
  const fieldsQ = useQuery<GroupField[]>({
    queryKey: key,
    queryFn: ({ signal }) => api.get<GroupField[]>(`/api/custom-fields/groups/${groupId}`, signal),
    enabled: !!groupId,
  });
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState('');
  const [dataType, setDataType] = useState('text');
  const [err, setErr] = useState<string | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  const defineMut = useMutation({
    mutationFn: () => api.post('/api/custom-fields/definitions', { group_id: groupId, label: label.trim(), data_type: dataType }),
    onSuccess: () => { setAdding(false); setLabel(''); setDataType('text'); setErr(null); invalidate(); },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'Could not add the field'),
  });
  const setValMut = useMutation({
    mutationFn: (p: { definition_id: string; value: string }) => api.put(`/api/custom-fields/groups/${groupId}`, p),
    onSuccess: invalidate,
  });

  const fields = fieldsQ.data ?? [];

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: compact ? 'var(--sp-2)' : 'var(--sp-3)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-3)' }}>
        <h2 style={{
          margin: 0,
          fontSize: compact ? 'var(--fs-sm)' : 'var(--fs-lg, 18px)',
          fontWeight: 'var(--fw-semibold)',
          color: compact ? 'var(--color-ink-2, var(--color-ink))' : 'var(--color-ink)',
        }}>{compact ? 'Fields' : 'Custom fields'}</h2>
        {canEdit && !adding && <Button size="sm" variant="ghost" onClick={() => { setAdding(true); setErr(null); }}>+ Add field</Button>}
      </div>
      {!compact && <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>Information that belongs to this group — like a review status or an owner.</p>}

      {adding && (
        <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap' }}>
          <input autoFocus value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Field name (e.g. Review status)" aria-label="Field name"
            style={{ flex: 1, minWidth: 160, padding: '8px 10px', fontSize: 'var(--fs-sm)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md,8px)', background: 'var(--color-card)', color: 'var(--color-ink)' }} />
          <select value={dataType} onChange={(e) => setDataType(e.target.value)} aria-label="Field type"
            style={{ padding: '8px 10px', fontSize: 'var(--fs-sm)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md,8px)', background: 'var(--color-card)', color: 'var(--color-ink)' }}>
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="date">Date</option>
            <option value="boolean">Yes / No</option>
          </select>
          <Button size="sm" variant="primary" disabled={!label.trim() || defineMut.isPending} onClick={() => defineMut.mutate()}>Add</Button>
          <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setLabel(''); }}>Cancel</Button>
        </div>
      )}
      {err && <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-status-red, #b5432e)' }}>{err}</p>}

      {fieldsQ.isLoading ? <Spinner size="sm" /> : fields.length === 0 && !adding ? (
        <p style={{ margin: 0, fontSize: compact ? 'var(--fs-sm)' : undefined, color: 'var(--color-ink-3)' }}>
          {compact ? 'No fields on this group yet.' : 'No custom fields on this group yet.'}
        </p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: compact ? 'minmax(110px, 170px) 1fr' : 'minmax(140px, 220px) 1fr', gap: 'var(--sp-2) var(--sp-3)', alignItems: 'center' }}>
          {fields.map((f) => (
            <GroupFieldRow key={f.definition_id} field={f} canEdit={canEdit} onSave={(value) => setValMut.mutate({ definition_id: f.definition_id, value })} />
          ))}
        </div>
      )}
    </section>
  );
}

function GroupFieldRow({ field, canEdit, onSave }: { field: GroupField; canEdit: boolean; onSave: (value: string) => void }) {
  const [val, setVal] = useState(field.value ?? '');
  const dirty = val !== (field.value ?? '');
  return (
    <>
      <label style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2, var(--color-ink))' }}>{field.label}</label>
      <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
        <input
          type={field.data_type === 'number' ? 'number' : field.data_type === 'date' ? 'date' : 'text'}
          value={val} onChange={(e) => setVal(e.target.value)} disabled={!canEdit} aria-label={field.label}
          style={{ flex: 1, minWidth: 100, padding: '6px 10px', fontSize: 'var(--fs-sm)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md,8px)', background: canEdit ? 'var(--color-card)' : 'var(--color-card-2, var(--color-card))', color: 'var(--color-ink)' }}
        />
        {canEdit && dirty && <Button size="sm" variant="primary" onClick={() => onSave(val)}>Save</Button>}
      </div>
    </>
  );
}
